import React, { useEffect, useState } from 'react';
import useTypedSelector from '../../common/hooks/useTypedSelector';
import useApi from '../../common/hooks/useApi';
import RenewRegistrationPresenter from './RenewRegistrationPresenter';
import FormVerificationResponse from '../../domain/components/verify-identity/FormVerificationResponse';
import ProfileVerificationStatusCd from '../../domain/enums/ProfileVerificationStatusCd';
import GroupDetailsResponse from 'features/group-details/GroupDetailsResponse';
import { useRouteMatch } from 'react-router';
import ActionStatus from 'common/types/ActionStatus';
import upload from 'domain/components/application/upload/upload';
import { useHistory } from 'react-router-dom';
import { RenewalFormData } from './types';

function RenewRegistration() {
  const { push: pushHistory } = useHistory();
  const { token, profile } = useTypedSelector((state) => ({
    token: state.auth.access_token,
    profile: state.profile.data,
  }));
  const [document1, setDocument1] = React.useState<File>();
  const [document2, setDocument2] = React.useState<File>();
  const [submitStatus, setSubmitStatus] = useState({
    submitStatus: ActionStatus.none,
    submitMesage: '',
  });

  const {
    params: { groupRefKey },
  } = useRouteMatch<{ groupRefKey: string }>();

  const [
    { data: formVerificationData, loading: formVerificationLoading },
    fetchVerification,
  ] = useApi<FormVerificationResponse>({
    url: `/profile/formVerification`,
    method: 'GET',
  });

  const [{ data: groupDetailData }, fetchRegistration] =
    useApi<GroupDetailsResponse>({
      url: `/applications/groups/${groupRefKey}`,
      method: 'GET',
    });

  useEffect(() => {
    fetchVerification();
    fetchRegistration();
  }, [fetchVerification, fetchRegistration]);

  const handleFileChange = (
    fileList: FileList | null,
    setState: React.Dispatch<React.SetStateAction<File | undefined>>
  ) => {
    if (!fileList) return;
    setState(fileList[0]);
  };

  const onSubmit = async (values: RenewalFormData) => {
    const groupRefKey = groupDetailData?.groupRefKey;
    const {
      attachmentType1Cd,
      attachmentType2Cd,
      primaryResIndicator,
      intermediaries,
      propertyRelaCd,
      dwellingTypeCd,
      howManyRooms,
    } = values;

    if (groupRefKey && token && propertyRelaCd && dwellingTypeCd) {
      const isPrimaryResidence = primaryResIndicator === 'Y';

      try {
        setSubmitStatus({
          submitStatus: ActionStatus.saving,
          submitMesage: '',
        });

        await upload(
          groupRefKey,
          token,
          '/applications/submit-renewal',
          isPrimaryResidence,
          dwellingTypeCd,
          howManyRooms,
          intermediaries,
          propertyRelaCd,
          isPrimaryResidence &&
            document1 &&
            document2 &&
            attachmentType1Cd &&
            attachmentType2Cd
            ? [
                {
                  files: document1,
                  attachmentTypeCd: attachmentType1Cd,
                  uploadTypeCd: 'PRIMARY_RESIDENCE',
                },
                {
                  files: document2,
                  attachmentTypeCd: attachmentType2Cd,
                  uploadTypeCd: 'PRIMARY_RESIDENCE_02',
                },
              ]
            : undefined
        );

        setSubmitStatus({
          submitStatus: ActionStatus.saved,
          submitMesage: 'Renewal Submitted',
        });
      } catch (e: any) {
        setSubmitStatus({
          submitStatus: ActionStatus.failed,
          submitMesage: e.toString(),
        });
      }
    }
  };

  const onReturnToDashboardClick = () => {
    pushHistory('/dashboard');
  };

  return (
    <>
      {groupDetailData && (
        <RenewRegistrationPresenter
          applicationDetails={groupDetailData}
          profile={profile}
          identity={{
            rejected:
              formVerificationData?.status !== null &&
              formVerificationData?.status ===
                ProfileVerificationStatusCd.REJECTED,
            uploaded:
              formVerificationData?.status !== null &&
              formVerificationData?.status !==
                ProfileVerificationStatusCd.INITIAL,
            loading: formVerificationLoading,
          }}
          allowedFileTypes={formVerificationData?.allowedFileTypes}
          isDocument1Uploaded={document1 !== undefined}
          isDocument2Uploaded={document2 !== undefined}
          submitStatus={submitStatus.submitStatus}
          submitMessage={submitStatus.submitMesage}
          onAttachment1Change={(e) => {
            handleFileChange(e.target.files, setDocument1);
          }}
          onAttachment2Change={(e) => {
            handleFileChange(e.target.files, setDocument2);
          }}
          onSubmit={onSubmit}
          onReturnToDashboardClick={onReturnToDashboardClick}
        />
      )}
    </>
  );
}

export default RenewRegistration;
